import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { Modal, View, StyleSheet, TouchableOpacity, TouchableWithoutFeedback } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

import Text from '../global/Text';
import QuestionnaireScrollPicker, { QuestionnaireScrollPickerHandle } from './QuestionnaireScrollPicker';
import theme from '../../constants/theme';
import fontMaker from '../../utils/font';
import { SCREEN_WIDTH } from '../../utils/screensize';

const TIME_ITEM_HEIGHT = 50;
const TIME_VISIBLE_COUNT = 3;
const TIME_ARROW_HEIGHT = 40;
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i.toString());
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i.toString());

export const DATE_FORMAT = 'DD-MM-YYYY';
export const DATE_TIME_FORMAT = 'DD-MM-YYYY HH:mm';

type QuestionnaireDateTimePickerModalProps = {
  visible: boolean;
  /** Current answer string (DD-MM-YYYY or DD-MM-YYYY HH:mm). */
  value?: string;
  onClose: () => void;
  /** Called with the formatted date string and whether time was excluded. */
  onSave: (formatted: string, isDateOnly: boolean) => void;
};

/** Parse the stored answer back into a moment, accepting both date-only and date-time formats. */
const parseValue = (value?: string): Moment | null => {
  if (!value) {
    return null;
  }
  const dateTime = moment(value, DATE_TIME_FORMAT, true);
  if (dateTime.isValid()) {
    return dateTime;
  }
  const dateOnly = moment(value, DATE_FORMAT, true);
  if (dateOnly.isValid()) {
    return dateOnly;
  }
  return null;
};

const QuestionnaireDateTimePickerModal = (props: QuestionnaireDateTimePickerModalProps) => {
  const { visible, value, onClose, onSave } = props;
  const { t } = useTranslation('questionnaire');

  const [currentMonth, setCurrentMonth] = useState<Moment>(moment());
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const [selectedHour, setSelectedHour] = useState<number>(0);
  const [selectedMinute, setSelectedMinute] = useState<number>(0);
  const [showTimer, setShowTimer] = useState<boolean>(false);

  const hourPickerRef = useRef<QuestionnaireScrollPickerHandle>(null);
  const minutePickerRef = useRef<QuestionnaireScrollPickerHandle>(null);

  // Sync internal state with the incoming value whenever the modal is (re)opened.
  useEffect(() => {
    if (!visible) {
      return;
    }
    const parsed = parseValue(value);
    if (parsed) {
      setSelectedDate(parsed.clone().startOf('day'));
      setSelectedHour(parsed.hour());
      setSelectedMinute(parsed.minute());
      setCurrentMonth(parsed.clone().startOf('month'));
    } else {
      setSelectedDate(null);
      setSelectedHour(0);
      setSelectedMinute(0);
      setCurrentMonth(moment().startOf('month'));
    }
    setShowTimer(false);
  }, [visible, value]);

  const weekdayLabels = useMemo(() => moment.weekdaysShort(), []);

  // Build a 6x7 grid starting from the Sunday on/before the 1st of the displayed month.
  const weeks = useMemo(() => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const gridStart = startOfMonth.clone().subtract(startOfMonth.day(), 'days');
    const rows: Moment[][] = [];
    const cursor = gridStart.clone();
    for (let week = 0; week < 6; week += 1) {
      const days: Moment[] = [];
      for (let day = 0; day < 7; day += 1) {
        days.push(cursor.clone());
        cursor.add(1, 'day');
      }
      rows.push(days);
    }
    return rows;
  }, [currentMonth]);

  const handlePrevMonth = () => setCurrentMonth(prev => prev.clone().subtract(1, 'month'));
  const handleNextMonth = () => setCurrentMonth(prev => prev.clone().add(1, 'month'));

  const handleSelectDay = (day: Moment) => {
    setSelectedDate(day.clone().startOf('day'));
  };

  const buildDateTime = () => {
    if (!selectedDate) {
      return null;
    }
    return selectedDate
      .clone()
      .hour(selectedHour)
      .minute(selectedMinute)
      .second(0)
      .millisecond(0);
  };

  const handleSaveDate = () => {
    const dateTime = buildDateTime();
    if (!dateTime) {
      return;
    }
    onSave(dateTime.format(DATE_FORMAT), true);
    onClose();
  };

  const handleSaveDateTime = () => {
    const dateTime = buildDateTime();
    if (!dateTime) {
      return;
    }
    onSave(dateTime.format(DATE_TIME_FORMAT), false);
    onClose();
  };

  // Arrow controls: top arrow moves to the row above (smaller), bottom arrow to the row below (larger).
  const adjustHour = (delta: number) => {
    const next = Math.max(0, Math.min(HOUR_OPTIONS.length - 1, selectedHour + delta));
    setSelectedHour(next);
    hourPickerRef.current?.scrollToIndex(next);
  };

  const adjustMinute = (delta: number) => {
    const next = Math.max(0, Math.min(MINUTE_OPTIONS.length - 1, selectedMinute + delta));
    setSelectedMinute(next);
    minutePickerRef.current?.scrollToIndex(next);
  };

  return (
    <Modal visible={visible} transparent animationType="fade">
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.root}>
          <TouchableWithoutFeedback onPress={() => undefined}>
            <View style={styles.container}>
              <View style={styles.header}>
                {showTimer ? (
                  <TouchableOpacity
                    onPress={() => setShowTimer(false)}
                    hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                    testID="datetime-back"
                  >
                    <Icon name="chevron-left" style={styles.headerIcon} />
                  </TouchableOpacity>
                ) : null}
                <Text style={[styles.headerTitle, showTimer ? styles.headerTitleCenter : styles.headerTitleLeft]}>
                  {showTimer ? t('question.dateTime.timePicker') : t('question.dateTime.datePicker')}
                </Text>
                <TouchableOpacity
                  onPress={onClose}
                  hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                  testID="datetime-close"
                >
                  <Icon name="close" style={styles.headerIcon} />
                </TouchableOpacity>
              </View>

              {showTimer ? (
                <View style={styles.timeContainer}>
                  <View style={styles.timeHighlight} pointerEvents="none" />
                  <View style={styles.timeRow}>
                    <View style={styles.timeColumn}>
                      <TouchableOpacity
                        style={styles.timeArrowButton}
                        onPress={() => adjustHour(-1)}
                        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                        testID="hour-up"
                      >
                        <Icon name="chevron-up" style={styles.timeArrow} />
                      </TouchableOpacity>
                      <QuestionnaireScrollPicker
                        ref={hourPickerRef}
                        style={styles.timePicker}
                        dataSource={HOUR_OPTIONS}
                        selectedIndex={selectedHour}
                        itemHeight={TIME_ITEM_HEIGHT}
                        visibleCount={TIME_VISIBLE_COUNT}
                        onValueChange={(_value, index) => setSelectedHour(index)}
                      />
                      <TouchableOpacity
                        style={styles.timeArrowButton}
                        onPress={() => adjustHour(1)}
                        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                        testID="hour-down"
                      >
                        <Icon name="chevron-down" style={styles.timeArrow} />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.timeColumn}>
                      <TouchableOpacity
                        style={styles.timeArrowButton}
                        onPress={() => adjustMinute(-1)}
                        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                        testID="minute-up"
                      >
                        <Icon name="chevron-up" style={styles.timeArrow} />
                      </TouchableOpacity>
                      <QuestionnaireScrollPicker
                        ref={minutePickerRef}
                        style={styles.timePicker}
                        dataSource={MINUTE_OPTIONS}
                        selectedIndex={selectedMinute}
                        itemHeight={TIME_ITEM_HEIGHT}
                        visibleCount={TIME_VISIBLE_COUNT}
                        onValueChange={(_value, index) => setSelectedMinute(index)}
                      />
                      <TouchableOpacity
                        style={styles.timeArrowButton}
                        onPress={() => adjustMinute(1)}
                        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                        testID="minute-down"
                      >
                        <Icon name="chevron-down" style={styles.timeArrow} />
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              ) : (
                <View style={styles.calendarBody}>
                  <View style={styles.monthNav}>
                    <TouchableOpacity
                      onPress={handlePrevMonth}
                      hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                      testID="prev-month"
                    >
                      <Icon name="chevron-left" style={styles.navIcon} />
                    </TouchableOpacity>
                    <Text style={styles.monthLabel}>{currentMonth.format('MMMM YYYY')}</Text>
                    <TouchableOpacity
                      onPress={handleNextMonth}
                      hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                      testID="next-month"
                    >
                      <Icon name="chevron-right" style={styles.navIcon} />
                    </TouchableOpacity>
                  </View>

                  <View style={styles.weekRow}>
                    {weekdayLabels.map(label => (
                      <View style={styles.dayCell} key={label}>
                        <Text style={styles.weekdayText}>{label}</Text>
                      </View>
                    ))}
                  </View>

                  {weeks.map((week, weekIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <View style={styles.weekRow} key={weekIndex}>
                      {week.map(day => {
                        const isCurrentMonth = day.month() === currentMonth.month();
                        const isSelected = !!selectedDate && day.isSame(selectedDate, 'day');
                        return (
                          <TouchableOpacity
                            style={styles.dayCell}
                            key={day.toISOString()}
                            onPress={() => handleSelectDay(day)}
                            testID={`day-${day.format('YYYY-MM-DD')}`}
                          >
                            <View style={[styles.dayBackground, isSelected && styles.daySelected]}>
                              <Text
                                style={[
                                  styles.dayText,
                                  !isCurrentMonth && styles.dayMuted,
                                  isSelected && styles.daySelectedText
                                ]}
                              >
                                {day.format('D')}
                              </Text>
                            </View>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  ))}
                </View>
              )}

              <View style={styles.footer}>
                {showTimer ? (
                  <TouchableOpacity
                    style={[styles.button, styles.buttonPrimary, !selectedDate && styles.buttonDisabled]}
                    onPress={handleSaveDateTime}
                    disabled={!selectedDate}
                    testID="datetime-save-time"
                  >
                    <Text style={styles.buttonPrimaryText}>{t('question.dateTime.save')}</Text>
                  </TouchableOpacity>
                ) : (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.buttonPrimary, !selectedDate && styles.buttonDisabled]}
                      onPress={handleSaveDate}
                      disabled={!selectedDate}
                      testID="datetime-save-date"
                    >
                      <Text style={styles.buttonPrimaryText}>{t('question.dateTime.save')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.button, styles.buttonOutline, !selectedDate && styles.buttonDisabled]}
                      onPress={() => setShowTimer(true)}
                      disabled={!selectedDate}
                      testID="datetime-choose-time"
                    >
                      <Text style={styles.buttonOutlineText}>{t('question.dateTime.chooseTime')}</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default QuestionnaireDateTimePickerModal;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    width: SCREEN_WIDTH > 500 ? 768 : SCREEN_WIDTH,
    marginHorizontal: 'auto'
  },
  container: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 28,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.36,
    shadowRadius: 6.68,
    elevation: 11
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12
  },
  headerTitle: {
    fontSize: 18,
    color: theme.styles.textDark.color,
    ...fontMaker({ weight: 'SemiBold' })
  },
  headerTitleLeft: {
    textAlign: 'left'
  },
  headerTitleCenter: {
    flex: 1,
    textAlign: 'center'
  },
  headerIcon: {
    fontSize: 24,
    color: '#4A4A4A'
  },
  calendarBody: {
    marginBottom: 8
  },
  monthNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12
  },
  monthLabel: {
    fontSize: 16,
    marginHorizontal: 24,
    color: theme.styles.textDark.color,
    ...fontMaker({ weight: 'SemiBold' })
  },
  navIcon: {
    fontSize: 26,
    color: theme.colors.primary
  },
  weekRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginVertical: 4
  },
  dayCell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },
  weekdayText: {
    fontSize: 12,
    color: '#8a8a8a',
    ...fontMaker({ weight: 'SemiBold' })
  },
  dayBackground: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center'
  },
  daySelected: {
    backgroundColor: theme.colors.primary
  },
  dayText: {
    fontSize: 14,
    color: theme.colors.primary
  },
  dayMuted: {
    color: '#c7c7c7'
  },
  daySelectedText: {
    color: '#fff',
    ...fontMaker({ weight: 'SemiBold' })
  },
  timeContainer: {
    position: 'relative',
    height: TIME_ARROW_HEIGHT * 2 + TIME_ITEM_HEIGHT * TIME_VISIBLE_COUNT,
    marginVertical: 12
  },
  timeHighlight: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: TIME_ARROW_HEIGHT + TIME_ITEM_HEIGHT * Math.floor(TIME_VISIBLE_COUNT / 2),
    height: TIME_ITEM_HEIGHT,
    backgroundColor: '#E0F0F0',
    borderRadius: 8
  },
  timeRow: {
    flexDirection: 'row'
  },
  timeColumn: {
    flex: 1,
    alignItems: 'center'
  },
  timePicker: {
    alignSelf: 'stretch'
  },
  timeArrowButton: {
    height: TIME_ARROW_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch'
  },
  timeArrow: {
    fontSize: 28,
    color: theme.colors.primary
  },
  footer: {
    flexDirection: 'row',
    marginTop: 12
  },
  button: {
    flex: 1,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 5
  },
  buttonPrimary: {
    backgroundColor: theme.colors.primary
  },
  buttonPrimaryText: {
    fontSize: 16,
    color: '#fff',
    ...fontMaker({ weight: 'SemiBold' })
  },
  buttonOutline: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: theme.colors.primary
  },
  buttonOutlineText: {
    fontSize: 16,
    color: theme.colors.primary,
    ...fontMaker({ weight: 'SemiBold' })
  },
  buttonDisabled: {
    opacity: 0.5
  }
});
